// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_plant_diary/<action>
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). The plant-care log stays local; only a DERIVED-
// SIGNALS slice (computed in signals.rs — per-plant intervals, gaps, overdue flags,
// and the single latest note) is sent for each narration call. The raw timeline never
// leaves the device.
//
// Two user actions, ONE billed AI call each (text-in scoped JSON → text-out, no agent
// loop, no tool-use, no 2nd call):
//   • "this-week"  — narrate the whole collection's watering rhythm (the home recap).
//   • "story"      — narrate ONE plant's care rhythm (its Story).
//
// The narrations (this-week / story) DESCRIBE the user's own care log — they name the
// figures in the supplied signals and never invent one.
//
// The free-form "Ask" is broader (rescoped 2026-07-09): a plant is not a person, so plant
// questions get an honest, SOURCED answer rather than a refusal. Only human/pet health &
// safety is hard-refused pre-LLM (see crossesHumanSafetyFence below). We compute the
// signals, call the brain, render the result — we do not rebuild the brain.

import { invoke } from "./tauri-web";
import { webContextFor } from "./search";
import { getAccessToken } from "./supabase";
import type { PlantSignals, WeekSignals, SpeciesId } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_plant_diary";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "refused"
  | "unavailable"
  | "no_store"
  | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(action: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── The fence, rescoped 2026-07-09 (Bogdan) ───────────────────────────────────
// "For people caring [for] their plants it's less liability, it's not human health."
//
// A plant is not a person. Plant care is NOT a regulated vertical (BRAIN-PATTERNS
// Pattern 5 Tier C = medical / legal / tax / financial), so the app no longer refuses
// "how's it doing", "why is it yellowing", "how often should I water it". It ANSWERS —
// as an honest summariser of what's commonly said, WITH real sources, never as an
// oracle and never prescribing a treatment as if it knew (Pattern 7, the sourcing gate).
//
// What stays HARD-refused is the one thing that IS about a person or an animal:
// toxicity / edibility / "is this mould dangerous" / "safe for my cat". That's human &
// pet health — a vet or a doctor answers it, not us. Everything else is in-domain.
//
// The old fence blocked "should i", "how often", "why is", "what's this", "is it normal"
// — i.e. nearly every natural plant question — which is exactly the over-correction
// Bogdan reversed when he locked the three-tier model.
const REFUSAL_MESSAGE =
  "That one's about health and safety, not the plant's diary — please ask a vet (for a pet) " +
  "or a doctor, or check a poison-control line. I can show you this plant's care history and " +
  "what it's been doing.";

// HUMAN / PET health + safety only. Everything here is a question about a body, not a plant.
const SAFETY_FENCE_PATTERNS: RegExp[] = [
  /\b(toxic|toxicity|poison|poisonous|edible|inedible)\b/i,
  /\bsafe (to eat|to touch|for (my )?(cat|dog|pet|pets|kid|kids|child|children|baby))\b/i,
  /\b(harmful|dangerous)\b.*\b(cat|dog|pet|pets|kid|kids|child|children|baby|me|human|breathe|breathing|lungs)\b/i,
  /\b(cat|dog|pet|pets|kid|kids|child|children|baby)\b.*\b(eat|ate|chewed|swallow|swallowed|licked)\b/i,
  /\b(mold|mould|fungus|spores)\b.*\b(dangerous|harmful|breathe|breathing|lungs|sick|health)\b/i,
];

/**
 * True only when the question is about a PERSON's or an ANIMAL's health/safety — the one
 * hard line left. Plant-health questions ("why is it yellowing") are NOT fenced; they get
 * a sourced, honest answer instead (see the server prompt's SOURCING rules).
 */
export function crossesHumanSafetyFence(question: string): boolean {
  const q = question.trim();
  if (!q) return false;
  return SAFETY_FENCE_PATTERNS.some((re) => re.test(q));
}

export interface Narration {
  text: string;
}

/**
 * "This week" — narrate the WHOLE collection's watering rhythm. ONE billed call.
 * Sends only the cross-plant derived signals (per-plant intervals + overdue flags,
 * ordered most-overdue first). Observational only; never advises or diagnoses.
 * Server endpoint: POST /api/apps/oriora_plant_diary/this-week  (operator task).
 */
export async function narrateThisWeek(signals: WeekSignals): Promise<Narration> {
  if (signals.plant_count === 0) {
    throw new BrainError("unavailable", "Add a plant and log some care first — there's nothing to narrate yet.");
  }
  const r = await brainCall<Partial<Narration>>("this-week", { signals });
  return { text: String(r.text ?? "").trim() };
}

/**
 * "Story" — narrate ONE plant's care rhythm. ONE billed call.
 * Sends only that plant's derived signals slice (its own intervals, gaps, overdue
 * flags + the single latest note). Observational only; never advises or diagnoses.
 * Server endpoint: POST /api/apps/oriora_plant_diary/story  (operator task).
 */
export async function narratePlantStory(signals: PlantSignals): Promise<Narration> {
  if (signals.total_events === 0) {
    throw new BrainError("unavailable", "Log some care for this plant first — there's nothing to narrate yet.");
  }
  const r = await brainCall<Partial<Narration>>("story", { signals });
  return { text: String(r.text ?? "").trim() };
}

/**
 * "Ask" — a free-form question about this plant. ONE billed call.
 * Answers from the supplied derived signals where the answer lives in the log; where it
 * doesn't, the brain summarises what's commonly said WITH real sources, or labels the
 * answer as its own fallible AI reasoning (Pattern 7). It never prescribes a treatment
 * as if it knew. Only human/pet health & safety is hard-refused, pre-LLM, below.
 * Server endpoint: POST /api/apps/oriora_plant_diary/ask
 */
export async function askAboutPlant(question: string, signals: PlantSignals): Promise<Narration> {
  const q = question.trim();
  if (!q) throw new BrainError("unavailable", "Ask something about this plant.");
  // The one hard line: a question about a PERSON's or PET's health. Refused free, pre-LLM.
  if (crossesHumanSafetyFence(q)) {
    throw new BrainError("refused", REFUSAL_MESSAGE);
  }
  // Option 3 (on-device web search): browse on the user's own device+internet, append the
  // results so the brain answers grounded + cited on ANY BYOK vendor. "" when nothing retrieved
  // → the Pattern-7 guard makes it fall back to honest own-reasoning. Never blocks the answer.
  const web = await webContextFor(q);
  const r = await brainCall<Partial<Narration>>("ask", { question: q + web, signals });
  return { text: String(r.text ?? "").trim() };
}

/**
 * Photo → species, through the ORIORA BRAIN. The router picks an image-capable model.
 *
 * Replaces (2026-07-27, §5d) the Rust `identify_species` command, which called a named vendor's
 * vision endpoint device-direct on a vendor key kept in an on-device file. No app names or calls
 * an AI vendor, and no key is handled inside an app. There is no key gate here any more — this is
 * an ordinary billed brain call like every other one in this file.
 * Server endpoint: POST /api/apps/oriora_plant_diary/identify-species
 */
export async function identifySpecies(photoDataUrl: string): Promise<SpeciesId> {
  const url = (photoDataUrl ?? "").trim();
  if (!url.startsWith("data:image/")) throw new BrainError("unavailable", "Attach a photo first.");
  const r = await brainCall<{ species?: unknown; confidence?: unknown }>("identify-species", { image: url });
  // Narrow to the union the UI renders — anything unrecognised reads as "low", which is the same
  // conservative default the server applies. Never widen SpeciesId to a bare string to make this
  // compile: "low" on an unparseable reply is the honest answer.
  const c = r.confidence;
  return {
    species: String(r.species ?? "").trim(),
    confidence: c === "high" || c === "medium" ? c : "low",
  };
}

// ── Care profile (the AI moment: a reasoned care plan for ANY plant) ────────────
// The MODEL is the knowledge — no shipped species table. The brain reasons a profile for the
// species, TUNES it to this plant's own observed rhythm (signals), and OVERRIDES with the owner's
// own rules if they wrote any in their connected cloud. The app caches the result on the plant and
// derives "what's due today" from water_days locally (date maths is free — we bill the reasoning).
export interface CareProfile {
  water_days: number;
  light: string;
  soil: string;
  humidity: string;
  feed: string;
  watch_for: string[];
  note: string;
}

export async function careProfile(
  species: string,
  location: string,
  signals: PlantSignals | null,
  ownerRules: string,
): Promise<CareProfile> {
  const r = await brainCall<Partial<CareProfile>>("care-profile", {
    species: (species ?? "").trim(),
    location: (location ?? "").trim(),
    signals: signals ?? null,
    owner_rules: (ownerRules ?? "").trim(),
  });
  const wd = Number(r.water_days);
  return {
    water_days: Number.isFinite(wd) && wd > 0 && wd < 120 ? Math.round(wd) : 10,
    light: String(r.light ?? "").trim(),
    soil: String(r.soil ?? "").trim(),
    humidity: String(r.humidity ?? "").trim(),
    feed: String(r.feed ?? "").trim(),
    watch_for: Array.isArray(r.watch_for) ? r.watch_for.filter((x) => typeof x === "string") : [],
    note: String(r.note ?? "").trim(),
  };
}

// ── Diagnose (the sick-plant moment: photo + THIS plant's history → likely causes) ──
// A photo of a struggling plant plus its own care log. The brain reasons over both — the log is the
// edge a static competitor library can't touch. The MODEL is the knowledge; no data layer.
export interface DiagnosisCause { cause: string; why: string; check: string }
export interface Diagnosis {
  summary: string;
  likely_causes: DiagnosisCause[];
  confidence: "high" | "medium" | "low";
  note: string;
}

export async function diagnose(
  species: string,
  photoDataUrl: string,
  signals: PlantSignals | null,
): Promise<Diagnosis> {
  const url = (photoDataUrl ?? "").trim();
  if (!url.startsWith("data:image/")) throw new BrainError("unavailable", "Add a photo of the plant first.");
  const r = await brainCall<Partial<Diagnosis>>("diagnose", { species: (species ?? "").trim(), image: url, signals: signals ?? null });
  const causes = Array.isArray(r.likely_causes) ? r.likely_causes : [];
  return {
    summary: String(r.summary ?? "").trim(),
    likely_causes: causes
      .filter((c): c is DiagnosisCause => !!c && typeof c === "object")
      .map((c) => ({ cause: String(c.cause ?? "").trim(), why: String(c.why ?? "").trim(), check: String(c.check ?? "").trim() }))
      .filter((c) => c.cause),
    confidence: r.confidence === "high" || r.confidence === "medium" ? r.confidence : "low",
    note: String(r.note ?? "").trim(),
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_plant_diary/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/**
 * Save ARBITRARY text — a note, or the whole conversation — to the customer's OWN cloud
 * (BRAIN-PATTERNS Pattern 5, Tier A "save anything to their own cloud"). The server relays it to
 * their connected GitHub/Drive under this app's folder and retains nothing; `provider` honours an
 * explicit "save it to my Drive". Text only — no bytes transit Oriora.
 * Endpoint: POST /api/apps/oriora_plant_diary/store
 */
export async function saveToCloud(
  name: string, content: string, kind: string, provider?: "github" | "gdrive",
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, content, kind, ...(provider ? { provider } : {}) }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't save that — please try again.");
  const j = (await res.json()) as { provider?: string };
  return { provider: String(j.provider ?? "your cloud") };
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `oriora-plant-diary` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

export { REFUSAL_MESSAGE };
